import { useEffect, useRef } from "react";

interface ScrollRevealOptions {
  stagger?: boolean;
  staggerDelay?: number;
}

export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options?: ScrollRevealOptions
) {
  const ref = useRef<T>(null);
  const { stagger = false, staggerDelay = 100 } = options || {};

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      el.style.opacity = "1";
      el.style.transform = "none";
      if (stagger) {
        Array.from(el.children).forEach((child) => {
          (child as HTMLElement).style.opacity = "1";
          (child as HTMLElement).style.transform = "none";
        });
      }
      return;
    }

    if (stagger) {
      // Hide children individually for staggered reveal
      el.style.opacity = "1";
      el.style.transform = "none";
      Array.from(el.children).forEach((child) => {
        const c = child as HTMLElement;
        c.style.opacity = "0";
        c.style.transform = "translateY(24px)";
        c.style.transition = "opacity 600ms ease-out, transform 600ms ease-out";
      });
    } else {
      el.style.opacity = "0";
      el.style.transform = "translateY(24px)";
      el.style.transition = "opacity 600ms ease-out, transform 600ms ease-out";
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (stagger) {
            Array.from(el.children).forEach((child, i) => {
              const c = child as HTMLElement;
              c.style.transitionDelay = `${i * staggerDelay}ms`;
              c.style.opacity = "1";
              c.style.transform = "translateY(0)";
            });
          } else {
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
          }
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [stagger, staggerDelay]);

  return ref;
}
